import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  Option,
  Table,
  TableRow,
} from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { PlasticCardRegistrationApi } from 'src/front/api/apiInstance';
import { PlasticCardOperationsSearchData, ReferenceDataType } from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import { OperationsPerformedSchema } from 'src/front/common/utils/validationSchemas';
import Datepicker from 'src/front/components/Datepicker';
import {
  ButtonRow,
  Container,
  FormElement,
  FormRow,
  LineFormElement,
  Separator,
  Subtitle,
  TableWrapper,
  Title,
} from 'src/front/components/FormStyles';
import { useUserInfoState } from 'src/front/contexts/SessionContext';

interface Props {
  setShowOperationPerformed: (showOperationPerformed: boolean) => void;
  pointOfSaleData: Option[];
}
export interface OperationsPerformedFormData {
  beginDate: Date | null;
  endDate: Date | null;
  pointOfSale: Option | null;
  accountType: Option | null;
}

const defaultValues = {
  beginDate: null,
  endDate: null,
  pointOfSale: null,
  accountType: null,
};

const operationTableHeaders = [
  'general_components_operations_label',
  'plasticCardRegistration_actNumber_label',
  'plasticCardRegistration_quantity_label',
  'general_components_transactionDate_label',
  'general_components_userCode_label',
  'plasticCardRegistration_comment_label',
  'general_components_direction_label',
];

const OperationsPerformed: FC<Props> = ({ setShowOperationPerformed, pointOfSaleData }) => {
  const { t } = useTranslation();
  const { processDate } = useUserInfoState();
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [showResult, setShowResult] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [operationListTableData, setOperationListTableData] = useState<
    PlasticCardOperationsSearchData[]
  >([]);
  const [selectedOperationRow, setSelectedOperationRow] = useState<TableRow>();
  const accountTypeData = usePRM(ReferenceDataType.PLASTICCARDOPERATIONSACCOUNTTYPE);
  const defaultProcessDate = processDate || null;
  const resolver = useYupResolver(OperationsPerformedSchema, { todayMaxControl: true });

  const { errors, handleSubmit, control, getValues } = useForm<OperationsPerformedFormData>({
    defaultValues: {
      ...defaultValues,
      beginDate: defaultProcessDate,
      endDate: defaultProcessDate,
    },
    resolver,
  });

  const onSubmit = async (): Promise<void> => {
    loadOperationList(1, true);
    setShowResult(true);
  };

  const loadOperationList = useCallback(
    async (pageNumber = page, forceRefresh = false) => {
      try {
        setTableLoading(true);
        const { beginDate, endDate, pointOfSale, accountType } = getValues();
        const { data } = await PlasticCardRegistrationApi.getPlasticCardOperationList({
          pointOfSale: pointOfSale?.value || '',
          accountType: accountType?.value || '',
          endDate: formatDate(endDate),
          startDate: formatDate(beginDate),
          refresh: !pageNumber || forceRefresh,
          rowCountPerPage: 10,
          pageNumber,
        });
        if (data.result) {
          setOperationListTableData(data.operationList);
          setHasNextPage(data.hasNextPage);
          setPage(pageNumber || 1);
        }
        setTableLoading(false);
      } catch (error) {
        setTableLoading(false);
      }
    },
    [getValues, page]
  );

  const goToBack = (): void => setShowOperationPerformed(false);

  const tableList: TableRow[] = operationListTableData.map((item, index) => ({
    id: item.userCode.toString() + index.toString(),
    columns: [
      item.operations,
      item.actNumber,
      item.quantity,
      item.transactionDate,
      item.userCode,
      item.comment,
      item.direction,
    ],
  }));

  return (
    <Container>
      <Title>
        <Trans i18nKey="plasticCardRegistration_title" />
      </Title>
      <Subtitle>
        <Trans i18nKey="plasticCardRegistration_operationsPerformed_subtitle" />
      </Subtitle>
      <Separator />
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <LineFormElement>
            <Controller
              control={control}
              name="pointOfSale"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.pointOfSale as FieldError)?.message as string)}
                  items={pointOfSaleData}
                  label={t('plasticCardRegistration_pointOfSale_label')}
                  name="pointOfSale"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </LineFormElement>
        </FormRow>
        <FormRow>
          <LineFormElement>
            <Controller
              control={control}
              name="accountType"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.accountType as FieldError)?.message as string)}
                  items={accountTypeData}
                  label={t('general_components_accountType_label')}
                  name="accountType"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </LineFormElement>
        </FormRow>
        <Separator />
        <Subtitle>
          <Trans i18nKey="general_components_transactionDate_text" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="beginDate"
              render={({ onChange, value }) => (
                <Datepicker
                  errorMessage={t(errors?.beginDate?.message as string)}
                  inputLabel={`${t('general_components_startDate_label')}`}
                  onChange={onChange}
                  value={value as Date}
                />
              )}
            />
          </FormElement>

          <FormElement>
            <Controller
              control={control}
              name="endDate"
              render={({ onChange, value }) => (
                <Datepicker
                  errorMessage={t(errors?.endDate?.message as string)}
                  inputLabel={`${t('general_components_endDate_label')}`}
                  onChange={onChange}
                  value={value as Date}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <Separator />
        <ButtonRow>
          <Button
            color={ButtonColor.Primary}
            disabled={tableLoading}
            size={ButtonSize.Medium}
            text={t('general_components_search_button')}
            type="submit"
          />
        </ButtonRow>
      </form>

      {showResult ? (
        <>
          <Separator />
          <Subtitle>
            <Trans i18nKey="general_components_operationList_text" />
          </Subtitle>

          <TableWrapper>
            <Table
              emptyTableText={t('general_components_notFound_errorMessage')}
              hasNextPage={hasNextPage}
              headers={operationTableHeaders.map((head) => t(head))}
              items={tableList}
              loading={tableLoading}
              loadPageItems={loadOperationList}
              onSelect={setSelectedOperationRow}
              pageNumber={page}
              selectedItem={selectedOperationRow}
              widths={['20', '12', '8', '10', '8', '12', '10']}
              clearItemOnPageChange
            />
          </TableWrapper>
        </>
      ) : null}
      <FormElement>
        <Button
          color={ButtonColor.Secondary}
          onClick={goToBack}
          size={ButtonSize.Medium}
          text={t('plasticCardRegistration_back_button')}
          type="button"
        />
      </FormElement>
    </Container>
  );
};

export default OperationsPerformed;
